import React, { useState } from 'react';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Check,
  BookOpen,
  MapPin,
  ShieldCheck,
  Target,
  SlidersHorizontal
} from 'lucide-react';
import { LearningModule, LearningProgress, LearningLesson } from '../../types';
import { Button } from '../UI/Button';

interface LearningPathOverviewProps {
  learningModules: LearningModule[];
  progress: LearningProgress;
  activeLessonId: string;
  onSelectLesson: (lessonId: string) => void;
  onNavigatePracticalAudit: () => void;
  initialSubView?: 'overview' | 'path';
}

export const LearningPathOverview: React.FC<LearningPathOverviewProps> = ({
  learningModules,
  progress,
  activeLessonId,
  onSelectLesson,
  onNavigatePracticalAudit,
  initialSubView = 'overview',
}) => {
  const [subView, setSubView] = useState<'overview' | 'path'>(initialSubView);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  // Flatten all lessons across modules
  const allLessons: LearningLesson[] = learningModules.flatMap(m => m.lessons);
  const totalLessons = allLessons.length;
  const completedCount = progress.completedLessonIds.length;
  const overallPct = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

  // Find active / current lesson object
  const currentLessonIndex = allLessons.findIndex(l => l.id === activeLessonId);
  const currentLesson = allLessons[currentLessonIndex] || allLessons[0];
  const activeModuleId = currentLesson?.moduleId;

  // Collapse state for Curriculum Roadmap
  const [collapsedModules, setCollapsedModules] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    learningModules.forEach((m) => {
      if (m.id === 'module-1-foundations' || m.id === activeModuleId) {
        initial[m.id] = false;
      } else {
        initial[m.id] = true;
      }
    });
    return initial;
  });

  // Calculate Gamification Rank & Level
  const xpPoints = completedCount * 100;
  const maxXp = totalLessons * 100;

  let rankTitle = 'SEO NOVICE';
  let rankLevel = 'Level 01';
  if (overallPct >= 100) {
    rankTitle = 'ZYNEX NINJA MASTER';
    rankLevel = 'Level 05';
  } else if (overallPct >= 75) {
    rankTitle = 'TECHNICAL AUDITOR';
    rankLevel = 'Level 04';
  } else if (overallPct >= 50) {
    rankTitle = 'LOCAL SEO APPRENTICE';
    rankLevel = 'Level 03';
  } else if (overallPct >= 25) {
    rankTitle = 'FOUNDATION STUDENT';
    rankLevel = 'Level 02';
  }

  const toggleModuleCollapse = (moduleId: string) => {
    setCollapsedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Module completion stats
  const getModuleStats = (modId: string) => {
    const mod = learningModules.find(m => m.id === modId);
    if (!mod) return { count: 0, completed: 0, pct: 0 };
    const completed = mod.lessons.filter(l => progress.completedLessonIds.includes(l.id)).length;
    const count = mod.lessons.length;
    const pct = count > 0 ? Math.round((completed / count) * 100) : 0;
    return { count, completed, pct };
  };

  const mod1Stats = getModuleStats('module-1-foundations');
  const mod2Stats = getModuleStats('module-2-local-intro');
  const mod3Stats = getModuleStats('module-3-pillars-structure');
  const mod4Stats = getModuleStats('module-4-onpage-seo');

  // Filter modules based on secondary category filter in Roadmap view
  const filteredModules = learningModules.filter(m => {
    if (activeCategory === 'all') return true;
    if (activeCategory === 'foundations') return m.id === 'module-1-foundations';
    if (activeCategory === 'local') return m.id === 'module-2-local-intro';
    if (activeCategory === 'pillars') return m.id === 'module-3-pillars-structure';
    if (activeCategory === 'onpage') return m.id === 'module-4-onpage-seo';
    if (activeCategory === 'content') return m.id === 'module-5-content-planning';
    return true;
  });

  return (
    <div className="animate-fade-in" style={{ width: '100%', maxWidth: '1180px', margin: '0 auto', padding: '16px 12px 80px 12px' }}>
      
      {/* ============================================================
          VIEW 1: DASHBOARD OVERVIEW (COMMAND CENTER ASYMMETRIC GRID)
         ============================================================ */}
      {subView === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* ROW 1: 3 TOP STATISTIC CARDS */}
          <div className="grid-responsive-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
            
            {/* Card 1: FOUNDATION PROGRESS */}
            <div
              style={{
                background: '#090e1a',
                border: '1px solid #1e293b',
                borderRadius: '14px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '120px',
              }}
            >
              <div>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  FOUNDATION PROGRESS
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                  <span style={{ fontSize: '32px', fontWeight: 900, color: '#ffffff', fontFamily: 'var(--font-mono)' }}>
                    {overallPct}%
                  </span>
                  <span style={{ fontSize: '13px', color: '#94a3b8' }}>
                    {completedCount} of {totalLessons} completed
                  </span>
                </div>
              </div>
              <div style={{ width: '100%', height: '5px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${overallPct}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
                    borderRadius: '3px',
                  }}
                />
              </div>
            </div>

            {/* Card 2: LESSONS COMPLETED */}
            <div
              style={{
                background: '#090e1a',
                border: '1px solid #1e293b',
                borderRadius: '14px',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '120px',
              }}
            >
              <div>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  LESSONS COMPLETED
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '32px', fontWeight: 900, color: '#ffffff', fontFamily: 'var(--font-mono)' }}>
                    {completedCount} / {totalLessons}
                  </span>
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {totalLessons - completedCount} remaining
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  Lectures 1–5 Technical Curriculum
                </div>
              </div>

              {/* Green Circular Book Icon */}
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <BookOpen size={22} color="#10b981" />
              </div>
            </div>

            {/* Card 3: SEO MASTERY LEVEL */}
            <div
              style={{
                background: '#090e1a',
                border: '1px solid #1e293b',
                borderRadius: '14px',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '120px',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  <SlidersHorizontal size={13} color="#64748b" />
                  <span>SEO MASTERY LEVEL</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '24px', fontWeight: 900, color: '#38bdf8', letterSpacing: '-0.02em' }}>
                    {rankTitle}
                  </span>
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'var(--font-mono)' }}>
                    {rankLevel}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {xpPoints} / {maxXp} XP Earned
                </div>
              </div>

              {/* Purple Badge Star Icon */}
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '50%',
                  background: 'rgba(168, 85, 247, 0.15)',
                  border: '1px solid rgba(168, 85, 247, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: '22px' }}>🎖️</span>
              </div>
            </div>

          </div>

          {/* ROW 2: CONTINUE LEARNING & TODAY'S LEARNING */}
          <div className="grid-responsive-12-8-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '18px' }}>
            
            {/* Left 8 Columns: CONTINUE LEARNING Card */}
            {currentLesson && (
              <div
                style={{
                  gridColumn: 'span 8',
                  background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.1) 0%, rgba(9, 14, 26, 0.95) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.35)',
                  borderLeft: '4px solid #0284c7',
                  borderRadius: '16px',
                  padding: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div style={{ flex: 1, zIndex: 2, minWidth: '240px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38bdf8', display: 'inline-block', boxShadow: '0 0 8px #38bdf8' }} />
                    <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      CONTINUE LEARNING
                    </span>
                    <span style={{ fontSize: '11px', color: '#475569' }}>•</span>
                    <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                      You're here
                    </span>
                  </div>

                  <h3 style={{ fontSize: '20px', color: '#ffffff', fontWeight: 800, margin: '0 0 10px 0', lineHeight: 1.3 }}>
                    Lesson {currentLesson.lessonNumber}: {currentLesson.title}
                  </h3>

                  <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: '0 0 16px 0', maxWidth: '440px', lineHeight: 1.5 }}>
                    {currentLesson.simpleExplanation || currentLesson.whatAreWeLearning}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#64748b', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <span>Lecture {currentLesson.lecture}</span>
                    <span>•</span>
                    <span>~10 min read</span>
                    <span>•</span>
                    <span style={{ color: '#38bdf8', fontWeight: 600, background: 'rgba(56, 189, 248, 0.12)', padding: '2px 8px', borderRadius: '4px' }}>
                      {currentLesson.topic}
                    </span>
                  </div>

                  <Button
                    size="lg"
                    variant="primary"
                    icon={<ArrowRight size={18} />}
                    onClick={() => onSelectLesson(currentLesson.id)}
                    style={{
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 700,
                      boxShadow: '0 4px 20px rgba(2, 132, 199, 0.4)',
                    }}
                  >
                    Continue Learning →
                  </Button>
                </div>

                {/* Right 3D Isometric SEO Browser Graphic Illustration */}
                <div className="desktop-only" style={{ flexShrink: 0, zIndex: 1, position: 'relative', width: '180px', height: '140px', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="170" height="130" viewBox="0 0 170 130" fill="none">
                    <rect x="20" y="20" width="130" height="90" rx="10" fill="#0f172a" stroke="#0284c7" strokeWidth="2" />
                    <rect x="20" y="20" width="130" height="24" rx="10" fill="#1e293b" />
                    <circle cx="34" cy="32" r="3.5" fill="#f43f5e" />
                    <circle cx="46" cy="32" r="3.5" fill="#f59e0b" />
                    <circle cx="58" cy="32" r="3.5" fill="#10b981" />
                    
                    <text x="35" y="75" fill="#38bdf8" fontSize="24" fontWeight="900" fontFamily="sans-serif" letterSpacing="1">
                      SEO
                    </text>

                    {/* Magnifying Glass */}
                    <circle cx="105" cy="78" r="18" stroke="#38bdf8" strokeWidth="4" fill="rgba(56, 189, 248, 0.1)" />
                    <line x1="118" y1="91" x2="135" y2="108" stroke="#38bdf8" strokeWidth="5" strokeLinecap="round" />
                    
                    {/* Small Gear / Graph accents */}
                    <rect x="115" y="48" width="8" height="14" rx="2" fill="#0284c7" />
                    <rect x="127" y="42" width="8" height="20" rx="2" fill="#38bdf8" />
                  </svg>
                </div>
              </div>
            )}

            {/* Right 4 Columns: TODAY'S LEARNING Card */}
            <div
              style={{
                gridColumn: 'span 4',
                background: '#090e1a',
                border: '1px solid #1e293b',
                borderRadius: '16px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
                  <SlidersHorizontal size={15} color="#38bdf8" />
                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    TODAY'S LEARNING
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid #38bdf8', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, color: '#ffffff' }}>Continue Lesson 1</div>
                      <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>What is SEO? (Search Engine + Optimization)</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid #475569', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, color: '#ffffff' }}>Review Search Engines & Guidelines</div>
                      <div style={{ fontSize: '11.5px', color: '#64748b' }}>Lecture 1 Fundamentals</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid #475569', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, color: '#ffffff' }}>Complete Foundation Checkpoint</div>
                      <div style={{ fontSize: '11.5px', color: '#64748b' }}>Comprehension Test</div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '11px', color: '#475569', paddingTop: '16px', borderTop: '1px solid #1e293b', marginTop: '16px' }}>
                Follow sequential curriculum to reach practical audits.
              </div>
            </div>

          </div>

          {/* ROW 3: YOUR LEARNING PROGRESS & MY LEARNING */}
          <div className="grid-responsive-12-8-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '18px' }}>
            
            {/* Left 4 Columns: YOUR LEARNING PROGRESS Card */}
            <div
              style={{
                gridColumn: 'span 4',
                background: '#090e1a',
                border: '1px solid #1e293b',
                borderRadius: '16px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '18px' }}>
                  YOUR LEARNING PROGRESS
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: '#ffffff' }}>SEO Foundations</span>
                      <span style={{ color: '#38bdf8', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{mod1Stats.pct}%</span>
                    </div>
                    <div style={{ width: '100%', height: '4px', background: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${mod1Stats.pct}%`, height: '100%', background: '#0284c7' }} />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: '#ffffff' }}>Local SEO Introduction</span>
                      <span style={{ color: '#38bdf8', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{mod2Stats.pct}%</span>
                    </div>
                    <div style={{ width: '100%', height: '4px', background: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${mod2Stats.pct}%`, height: '100%', background: '#0284c7' }} />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: '#ffffff' }}>Pillars & Site Structure</span>
                      <span style={{ color: '#38bdf8', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{mod3Stats.pct}%</span>
                    </div>
                    <div style={{ width: '100%', height: '4px', background: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${mod3Stats.pct}%`, height: '100%', background: '#0284c7' }} />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: '#ffffff' }}>On-Page SEO & Architecture</span>
                      <span style={{ color: '#38bdf8', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{mod4Stats.pct}%</span>
                    </div>
                    <div style={{ width: '100%', height: '4px', background: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${mod4Stats.pct}%`, height: '100%', background: '#0284c7' }} />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: '#64748b' }}>Practical Client Audits</span>
                      <span style={{ color: '#10b981', fontWeight: 700 }}>Destination</span>
                    </div>
                    <div style={{ width: '100%', height: '4px', background: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: '0%', height: '100%', background: '#10b981' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Progress Donut Circle & Legend */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', paddingTop: '16px', borderTop: '1px solid #1e293b' }}>
                {/* SVG Circular Ring Chart */}
                <div style={{ position: 'relative', width: '70px', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="70" height="70" viewBox="0 0 70 70">
                    <circle cx="35" cy="35" r="28" fill="none" stroke="#1e293b" strokeWidth="6" />
                    <circle
                      cx="35"
                      cy="35"
                      r="28"
                      fill="none"
                      stroke="#0284c7"
                      strokeWidth="6"
                      strokeDasharray="175"
                      strokeDashoffset={175 - (175 * overallPct) / 100}
                      strokeLinecap="round"
                      transform="rotate(-90 35 35)"
                    />
                  </svg>
                  <div style={{ position: 'absolute', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: 900, color: '#ffffff', fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
                      {overallPct}%
                    </div>
                    <div style={{ fontSize: '8px', color: '#64748b', marginTop: '2px' }}>
                      Overall
                    </div>
                  </div>
                </div>

                {/* Donut Legend */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11.5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#10b981' }} />
                    <span style={{ color: '#94a3b8' }}>Completed</span>
                    <span style={{ color: '#ffffff', fontWeight: 700, marginLeft: 'auto' }}>{overallPct}%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#0284c7' }} />
                    <span style={{ color: '#94a3b8' }}>In Progress</span>
                    <span style={{ color: '#ffffff', fontWeight: 700, marginLeft: 'auto' }}>{overallPct > 0 ? 100 - overallPct : 0}%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#475569' }} />
                    <span style={{ color: '#94a3b8' }}>Upcoming</span>
                    <span style={{ color: '#ffffff', fontWeight: 700, marginLeft: 'auto' }}>{100 - overallPct}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right 8 Columns: MY LEARNING Table Card */}
            <div
              style={{
                gridColumn: 'span 8',
                background: '#090e1a',
                border: '1px solid #1e293b',
                borderRadius: '16px',
                padding: '24px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  MY LEARNING
                </div>
                <button
                  onClick={() => setSubView('path')}
                  style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}
                >
                  View Full Roadmap →
                </button>
              </div>

              {/* Module Cards Stack */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                
                {/* Module 1 Row */}
                <div
                  onClick={() => setSubView('path')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        background: 'rgba(2, 132, 199, 0.15)',
                        border: '1px solid rgba(2, 132, 199, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <BookOpen size={18} color="#38bdf8" />
                    </div>
                    <div>
                      <div style={{ fontSize: '14.5px', fontWeight: 700, color: '#ffffff' }}>
                        Module 01: SEO Foundations & Orientation
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        6 lessons · Lectures 1–2
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8', fontFamily: 'var(--font-mono)' }}>
                      {mod1Stats.pct}%
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#38bdf8',
                        background: 'rgba(2, 132, 199, 0.2)',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(56, 189, 248, 0.3)',
                      }}
                    >
                      Current
                    </span>
                  </div>
                </div>

                {/* Module 2 Row */}
                <div
                  onClick={() => setSubView('path')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        background: 'rgba(30, 41, 59, 0.5)',
                        border: '1px solid #1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <MapPin size={18} color="#64748b" />
                    </div>
                    <div>
                      <div style={{ fontSize: '14.5px', fontWeight: 700, color: '#ffffff' }}>
                        Module 02: Local SEO Introduction & Roadmap
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        7 lessons · Lecture 3
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#94a3b8', fontFamily: 'var(--font-mono)' }}>
                      {mod2Stats.pct}%
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#94a3b8',
                        background: 'rgba(30, 41, 59, 0.6)',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: '1px solid #1e293b',
                      }}
                    >
                      Next
                    </span>
                  </div>
                </div>

                {/* Module 3 Row */}
                <div
                  onClick={() => setSubView('path')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        background: 'rgba(30, 41, 59, 0.5)',
                        border: '1px solid #1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <ShieldCheck size={18} color="#64748b" />
                    </div>
                    <div>
                      <div style={{ fontSize: '14.5px', fontWeight: 700, color: '#ffffff' }}>
                        Module 03: Local SEO Pillars & Site Structure
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        5 lessons · Lecture 4
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#94a3b8', fontFamily: 'var(--font-mono)' }}>
                      {mod3Stats.pct}%
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#64748b',
                        background: 'rgba(30, 41, 59, 0.4)',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: '1px solid #1e293b',
                      }}
                    >
                      Upcoming
                    </span>
                  </div>
                </div>

                {/* Module 4 Row */}
                <div
                  onClick={() => setSubView('path')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        background: 'rgba(30, 41, 59, 0.5)',
                        border: '1px solid #1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <BookOpen size={18} color="#64748b" />
                    </div>
                    <div>
                      <div style={{ fontSize: '14.5px', fontWeight: 700, color: '#ffffff' }}>
                        Module 04: On-Page SEO Implementation & URL/NAP Architecture
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        5 lessons · Lecture 5
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#94a3b8', fontFamily: 'var(--font-mono)' }}>
                      {mod4Stats.pct}%
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#64748b',
                        background: 'rgba(30, 41, 59, 0.4)',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: '1px solid #1e293b',
                      }}
                    >
                      Upcoming
                    </span>
                  </div>
                </div>

                {/* Module 4 Destination Row */}
                <div
                  onClick={onNavigatePracticalAudit}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    background: 'rgba(16, 185, 129, 0.08)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        background: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Target size={18} color="#10b981" />
                    </div>
                    <div>
                      <div style={{ fontSize: '14.5px', fontWeight: 700, color: '#ffffff' }}>
                        Module 04: Practical Client Audits
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                        32 technical criteria · Lectures 31–33
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#10b981',
                        background: 'rgba(16, 185, 129, 0.15)',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                      }}
                    >
                      Destination
                    </span>
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* ROW 4: PRACTICAL CLIENT AUDITS BOTTOM DESTINATION BANNER */}
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 9, 18, 0.95) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.35)',
              borderRadius: '16px',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '20px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ flex: 1, zIndex: 2, minWidth: '240px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontSize: '10.5px', fontWeight: 800, color: '#10b981', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  FINAL STAGE
                </span>
                <span style={{ color: '#475569', fontSize: '12px' }}>•</span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  PRACTICAL CLIENT AUDITS
                </span>
              </div>

              <h3 style={{ fontSize: '22px', color: '#ffffff', fontWeight: 800, margin: '0 0 10px 0' }}>
                Zynex Ninja Client Audit Engine
              </h3>

              <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, maxWidth: '560px', lineHeight: 1.5 }}>
                You've completed the foundation. Now apply everything to real websites across 32 technical criteria (Screaming Frog, Moz, SSL, W3C, Pingdom) and export client PDF reports.
              </p>
            </div>

            {/* Center 3D Laptop Graphic */}
            <div className="desktop-only" style={{ width: '160px', height: '110px', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
              <svg width="150" height="100" viewBox="0 0 150 100" fill="none">
                <rect x="25" y="15" width="100" height="65" rx="6" fill="#0f172a" stroke="#10b981" strokeWidth="2" />
                <rect x="15" y="80" width="120" height="8" rx="4" fill="#1e293b" />
                <path d="M40 60 L60 45 L80 52 L105 32" stroke="#10b981" strokeWidth="3" strokeLinecap="round" />
                <circle cx="105" cy="32" r="4" fill="#10b981" />
                <circle cx="75" cy="48" r="16" fill="rgba(16, 185, 129, 0.2)" stroke="#10b981" strokeWidth="2" />
                <path d="M70 48 L73 52 L80 44" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {/* Right Action Column & Points */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', zIndex: 2, flexShrink: 0 }}>
              <Button
                size="lg"
                variant="success"
                icon={<ArrowRight size={18} />}
                onClick={onNavigatePracticalAudit}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 800,
                  boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)',
                }}
              >
                Launch Practical Audit Engine →
              </Button>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px', color: '#94a3b8' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Check size={14} color="#10b981" strokeWidth={3} />
                  <span>32 Practical Audit Criteria</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Check size={14} color="#10b981" strokeWidth={3} />
                  <span>Automated Issue Scoring</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Check size={14} color="#10b981" strokeWidth={3} />
                  <span>Executive PDF Reports</span>
                </div>
              </div>
            </div>

          </div>

        </div>
      )}

      {/* ============================================================
          VIEW 2: FULL CURRICULUM ROADMAP (DETAILED PATH VIEW)
         ============================================================ */}
      {subView === 'path' && (
        <div>
          {/* Secondary Category Filter Bar for Roadmap */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            <div
              className="header-tabs-scroll"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: '#090e1a',
                padding: '6px',
                borderRadius: '10px',
                border: '1px solid #1e293b',
                overflowX: 'auto',
                maxWidth: '100%',
              }}
            >
              <button
                onClick={() => setActiveCategory('all')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: activeCategory === 'all' ? '#0284c7' : 'transparent',
                  color: activeCategory === 'all' ? '#ffffff' : '#94a3b8',
                }}
              >
                All Modules ({totalLessons})
              </button>
              <button
                onClick={() => setActiveCategory('foundations')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: activeCategory === 'foundations' ? '#0284c7' : 'transparent',
                  color: activeCategory === 'foundations' ? '#ffffff' : '#94a3b8',
                }}
              >
                01 Foundations
              </button>
              <button
                onClick={() => setActiveCategory('local')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: activeCategory === 'local' ? '#0284c7' : 'transparent',
                  color: activeCategory === 'local' ? '#ffffff' : '#94a3b8',
                }}
              >
                02 Local SEO
              </button>
              <button
                onClick={() => setActiveCategory('pillars')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: activeCategory === 'pillars' ? '#0284c7' : 'transparent',
                  color: activeCategory === 'pillars' ? '#ffffff' : '#94a3b8',
                }}
              >
                03 Pillars & Structure
              </button>
              <button
                onClick={() => setActiveCategory('onpage')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: activeCategory === 'onpage' ? '#0284c7' : 'transparent',
                  color: activeCategory === 'onpage' ? '#ffffff' : '#94a3b8',
                }}
              >
                04 On-Page SEO
              </button>
            </div>

            <button
              onClick={() => setSubView('overview')}
              style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
            >
              ← Back to Dashboard Overview
            </button>
          </div>

          {/* Modules Stack */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {filteredModules.map((module, modIdx) => {
              const isCollapsed = !!collapsedModules[module.id];
              const modCompletedCount = module.lessons.filter(l => progress.completedLessonIds.includes(l.id)).length;
              const modTotalCount = module.lessons.length;
              const modPct = modTotalCount > 0 ? Math.round((modCompletedCount / modTotalCount) * 100) : 0;
              const isModComplete = modPct === 100;

              return (
                <div
                  key={module.id}
                  style={{
                    background: '#090e1a',
                    border: '1px solid #1e293b',
                    borderRadius: '14px',
                    padding: '24px',
                  }}
                >
                  {/* Module Header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                      paddingBottom: isCollapsed ? '0' : '18px',
                      borderBottom: isCollapsed ? 'none' : '1px solid #1e293b',
                    }}
                    onClick={() => toggleModuleCollapse(module.id)}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 800,
                            color: isModComplete ? '#10b981' : '#38bdf8',
                            background: isModComplete ? 'rgba(16, 185, 129, 0.15)' : 'rgba(2, 132, 199, 0.15)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.8px',
                          }}
                        >
                          MODULE 0{modIdx + 1}
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          {modTotalCount} lessons · {modPct}% complete
                        </span>
                      </div>

                      <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: '0 0 4px 0' }}>
                        {module.name}
                      </h3>

                      <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0, maxWidth: '680px', lineHeight: 1.4 }}>
                        {module.description}
                      </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                      <div style={{ textAlign: 'right', minWidth: '90px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: isModComplete ? '#10b981' : '#ffffff', fontFamily: 'var(--font-mono)' }}>
                          {modPct}%
                        </div>
                        <div style={{ width: '90px', height: '4px', background: '#1e293b', borderRadius: '2px', overflow: 'hidden', marginTop: '4px' }}>
                          <div
                            style={{
                              width: `${modPct}%`,
                              height: '100%',
                              background: isModComplete ? '#10b981' : '#0284c7',
                              borderRadius: '2px',
                            }}
                          />
                        </div>
                      </div>

                      <button
                        style={{
                          background: '#1e293b',
                          border: '1px solid #334155',
                          color: '#ffffff',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                        }}
                      >
                        <span>{isCollapsed ? 'Expand module' : 'Collapse'}</span>
                        {isCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                      </button>
                    </div>
                  </div>

                  {/* Lessons List */}
                  {!isCollapsed && (
                    <div style={{ marginTop: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {module.lessons.map((lesson) => {
                        const isCompleted = progress.completedLessonIds.includes(lesson.id);
                        const isCurrent = lesson.id === activeLessonId;

                        return (
                          <div
                            key={lesson.id}
                            onClick={() => onSelectLesson(lesson.id)}
                            className="interactive-node"
                            style={{
                              background: isCurrent
                                ? 'rgba(2, 132, 199, 0.15)'
                                : isCompleted
                                ? '#040711'
                                : '#040711',
                              border: isCurrent
                                ? '1px solid #38bdf8'
                                : isCompleted
                                ? '1px solid rgba(16, 185, 129, 0.25)'
                                : '1px solid #1e293b',
                              borderRadius: '10px',
                              padding: '14px 18px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '16px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1 }}>
                              <div
                                style={{
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '50%',
                                  background: isCompleted
                                    ? '#10b981'
                                    : isCurrent
                                    ? '#0284c7'
                                    : '#1e293b',
                                  border: isCompleted
                                    ? '1px solid #10b981'
                                    : isCurrent
                                    ? '2px solid #38bdf8'
                                    : '1px solid #334155',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  marginTop: '2px',
                                }}
                              >
                                {isCompleted ? (
                                  <Check size={13} color="#ffffff" strokeWidth={3} />
                                ) : isCurrent ? (
                                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#ffffff' }} />
                                ) : (
                                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#64748b' }} />
                                )}
                              </div>

                              <div>
                                <div style={{ fontSize: '14.5px', fontWeight: 600, color: isCurrent ? '#ffffff' : isCompleted ? '#e2e8f0' : '#ffffff', marginBottom: '3px' }}>
                                  Lesson {lesson.lessonNumber}: {lesson.title}
                                </div>

                                <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '4px', lineHeight: 1.4 }}>
                                  {lesson.simpleExplanation}
                                </div>

                                <div style={{ fontSize: '11px', color: '#64748b' }}>
                                  Lecture {lesson.lecture} · ~10 min read
                                </div>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: isCompleted ? '#10b981' : isCurrent ? '#38bdf8' : '#94a3b8' }}>
                                {isCompleted ? 'Completed' : isCurrent ? 'Continue →' : 'Available →'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};
